import React, { useState } from 'react';
import { CSSObject } from '@emotion/react';
import TextLink from '@cvent/carina/components/TextLink';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import CventSVG from '@illustrations/CventSVG';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { CodeSnippet, EventLocale, LinkData } from '@graphql/gql/graphql';
import { withMoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import Icon from '@components/shared/Icon';
import ManageCookiePreference from '@components/shared/ManageCookiePreference';
import CookieConsentBannerWithModal, { getConsentChoices } from '@context/CookieConsentProvider';
import { CookieConsentProviderWrapper } from '@cvent/cookieconsent';
import CodeSnippetsWithCategory from '@components/app/CodeSnippetsWithCategory';
import LanguageSelector from './LanguageSelector';
import UpdateCookieConsentVar from './UpdateCookieConsentVar';

interface FooterWithImageProps {
  eventLink?: string;
  hideCventFooter?: boolean;
  eventPrivacyPolicy?: LinkData;
  cventPrivacyPolicyText?: string;
  footerImageSrc?: string;
  footerImageAltText?: string;
  supportedEventLocales?: EventLocale[];
  setLocale?: (locale: string) => void;
  locale?: string;
  eventId?: string;
  ccpaUrl?: string;
  enableManageCookiePreferences?: boolean;
  showCookieBanner?: boolean;
  showNonEssentialCookieBanner?: boolean;
  hidePrivacyPolicyLink?: boolean;
  codeSnippets: CodeSnippet[];
}

interface FooterWithImageStyles {
  imageContainerStyle: CSSObject;
  containerStyle: CSSObject;
  leftContainerStyle: CSSObject;
  rightContainerStyle: CSSObject;
  cventLogoStyle: CSSObject;
  cventTextStyle: CSSObject;
  cventLogoColor: string;
}

const useStyles = (): FooterWithImageStyles => {
  const { isMobile } = useBreakpoints();
  const { font, block } = useTheme();
  return {
    imageContainerStyle: {
      display: 'flex',
      alignItems: 'flex-end'
    },
    containerStyle: {
      fontSize: font.base.size.s,
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'space-between',
      padding: '8px 24px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    leftContainerStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 8
    },
    rightContainerStyle: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'flex-end',
      gap: 8
    },
    cventLogoStyle: {
      margin: isMobile ? '0px 8px 0px 8px' : '0px 16px'
    },
    cventLogoColor: block.isWhite || block.isLight ? null : font.color.base,
    cventTextStyle: { paddingTop: 2, whiteSpace: 'nowrap' }
  };
};

function FooterWithImage({
  eventLink,
  hideCventFooter,
  eventPrivacyPolicy,
  cventPrivacyPolicyText,
  footerImageSrc,
  footerImageAltText,
  supportedEventLocales = [],
  setLocale,
  locale,
  eventId,
  ccpaUrl,
  enableManageCookiePreferences,
  showCookieBanner,
  showNonEssentialCookieBanner,
  hidePrivacyPolicyLink,
  codeSnippets
}: FooterWithImageProps): JSX.Element {
  const { translate } = useTranslate();
  const [year] = useState(new Date().getFullYear());
  const consentChoices = getConsentChoices(eventId);
  const {
    containerStyle,
    leftContainerStyle,
    cventLogoStyle,
    cventLogoColor,
    imageContainerStyle,
    rightContainerStyle,
    cventTextStyle
  } = useStyles();

  const eventLinkComponent = eventLink ? (
    <TextLink href={eventLink} neutral target="_blank" rel="noopener noreferrer" testID="footer-event-website-link">
      {translate('Footer-EventWebsiteLink')}
    </TextLink>
  ) : null;

  const cventPrivacyPolicy = cventPrivacyPolicyText && (
    <TextLink
      href="https://www.cvent.com/cvent-global-privacy-policy"
      neutral
      target="_blank"
      rel="noopener noreferrer"
      testID="footer-privacy-policy"
      icon={<Icon name="external-link" size="xs" aria-hidden />}
      aria-label={cventPrivacyPolicyText || translate('Footer-PrivacyPolicy')}
    >
      {cventPrivacyPolicyText || translate('Footer-PrivacyPolicy')}
    </TextLink>
  );

  const customPrivacy = eventPrivacyPolicy ? (
    <TextLink
      href={eventPrivacyPolicy.url}
      neutral
      target="_blank"
      rel="noopener noreferrer"
      testID="footer-custom-privacy-policy"
      icon={<Icon name="external-link" size="xs" aria-hidden />}
      aria-label={eventPrivacyPolicy.text}
    >
      {eventPrivacyPolicy.text}
    </TextLink>
  ) : null;

  const ccpa = ccpaUrl ? (
    <TextLink
      href={ccpaUrl}
      neutral
      target="_blank"
      rel="noopener noreferrer"
      testID="footer-ccpa-url"
      icon={<Icon name="external-link" size="xs" aria-hidden />}
      aria-label={translate('footer_ccpa_link')}
    >
      {translate('footer_ccpa_link')}
    </TextLink>
  ) : null;

  const languageSelector =
    supportedEventLocales?.length > 1 ? (
      <LanguageSelector
        eventLocaleInfo={supportedEventLocales}
        setLocale={setLocale}
        locale={locale}
        eventId={eventId}
      />
    ) : null;

  const manageCookiePreferences = enableManageCookiePreferences ? <ManageCookiePreference /> : null;

  const links = [
    {
      content: eventLinkComponent,
      key: 'event-link'
    },
    {
      content: manageCookiePreferences,
      key: 'manage-cookie-preferences'
    },
    {
      content: cventPrivacyPolicy,
      key: 'cvent-privacy-policy'
    },
    {
      content: customPrivacy,
      key: 'custom-privacy-policy'
    },
    {
      content: ccpa,
      key: 'ccpa'
    },
    {
      content: languageSelector,
      key: 'language-selector'
    }
  ].filter(link => link.content);

  return (
    <CookieConsentProviderWrapper
      siteIdentifier={eventId}
      key={String(consentChoices === null || consentChoices === false)}
    >
      <div {...injectTestId('footer')}>
        {footerImageSrc ? (
          <div css={imageContainerStyle}>
            <img
              src={footerImageSrc}
              alt={footerImageAltText || ''}
              css={{ width: '100%', height: 'auto' }}
              {...injectTestId('footer-image')}
            />
          </div>
        ) : null}
        <div css={containerStyle}>
          <div css={leftContainerStyle}>
            {!hideCventFooter ? (
              <>
                <div css={cventTextStyle} {...injectTestId('powered-by-footer')}>
                  {translate('Footer-PoweredBy')}
                </div>
                <div css={cventLogoStyle}>
                  <CventSVG color={cventLogoColor} small />
                </div>
                <div css={cventTextStyle}>{translate('Footer-Copyright', { year })}</div>
              </>
            ) : null}
          </div>
          <div css={rightContainerStyle}>
            {links.map((link, index) => (
              <React.Fragment key={link.key}>
                {link.content}
                {links.length > 1 && index !== links.length - 1 && <span>&nbsp;|&nbsp;</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
        <CookieConsentBannerWithModal
          eventId={eventId}
          showCookieBanner={showCookieBanner}
          showNonEssentialCookieBanner={showNonEssentialCookieBanner}
          hidePrivacyPolicyLink={hidePrivacyPolicyLink}
        />
      </div>
      <CodeSnippetsWithCategory
        codeSnippets={codeSnippets}
        showCookieBanner={showCookieBanner}
        showNonEssentialCookieBanner={showNonEssentialCookieBanner}
      />
      <UpdateCookieConsentVar />
    </CookieConsentProviderWrapper>
  );
}

export default withMoodThemeProvider(FooterWithImage);
